import { useRef, useState, useEffect, useImperativeHandle, cloneElement, forwardRef } from 'react';
import { useKeyPress } from '../../utils/hooks/useKeyPress.js';
import { jsxs, jsx } from '@emotion/react/jsx-runtime';
import { useUtils } from './hooks/useUtils.js';
import { useOptionListData } from './hooks/useOptionListData.js';
import { Dropdown } from '../Dropdown/Dropdown.js';
import { OptionListHeader } from '../OptionListHeader/OptionListHeader.js';
import { OptionList } from '../OptionList/OptionList.js';
import { OptionGroup } from '../OptionGroup/OptionGroup.js';

function DropdownOptionListForwarded(_ref, ref) {
  let {
    children,
    isOpen,
    maxVisibleOptions = 5,
    optionsHeaderText,
    placement = 'bottom-start',
    resetFocusOnSelect = true,
    reference,
    onChange,
    onClickAway,
    onEscape,
    ...rest
  } = _ref;
  const {
    getValueString
  } = useUtils();
  const {
    childrensArray,
    optionsArray
  } = useOptionListData(children);
  const selectedElement = useRef(optionsArray[0]);
  const [focusedIndex, setFocusedIndex] = useState(undefined);
  useEffect(() => {
    if (focusedIndex !== undefined) {
      selectedElement.current = optionsArray[focusedIndex];
    }
  }, [focusedIndex, optionsArray]);
  const handleOnTab = event => event.preventDefault();
  const handleArrowUp = () => {
    setFocusedIndex(current => {
      const value = current === undefined ? 0 : current;
      return optionsArray[value - 1] ? value - 1 : value;
    });
  };
  const handleArrowDown = () => {
    setFocusedIndex(current => {
      const value = current === undefined ? -1 : current;
      return optionsArray[value + 1] ? value + 1 : value;
    });
  };
  const handleOnEnter = event => {
    event.preventDefault();
    if (!isOpen) return;
    onChange(selectedElement.current);
    setFocusedIndex(undefined);
  };
  const {
    handleKeyPress
  } = useKeyPress({
    onArrowDown: handleArrowDown,
    onArrowUp: handleArrowUp,
    onTab: handleOnTab,
    onEnter: handleOnEnter,
    onEscape: onEscape
  });
  useImperativeHandle(ref, () => ({
    onKeyDown: event => {
      handleKeyPress(event);
    }
  }));
  const handleMouseEnter = selectedOption => {
    const selectedOptionValue = getValueString(selectedOption);
    const selectedOptionIndex = optionsArray.findIndex(_ref2 => {
      let {
        value
      } = _ref2;
      return value === selectedOptionValue;
    });
    setFocusedIndex(selectedOptionIndex);
  };
  const handleOnOptionListMouseLeave = () => setFocusedIndex(undefined);
  const handleOnClickAway = event => {
    setFocusedIndex(undefined);
    onClickAway === null || onClickAway === void 0 ? void 0 : onClickAway(event);
  };
  const isOptionFocused = _ref3 => {
    var _optionsArray$focused;
    let {
      props: {
        value
      }
    } = _ref3;
    if (focusedIndex === undefined) {
      return false;
    }
    const selectedOptionValue = getValueString(value);
    return ((_optionsArray$focused = optionsArray[focusedIndex]) === null || _optionsArray$focused === void 0 ? void 0 : _optionsArray$focused.value) === selectedOptionValue;
  };
  return jsxs(Dropdown, {
    isOpen: isOpen,
    onClickAway: handleOnClickAway,
    placement: placement,
    reference: reference,
    ...rest,
    children: [optionsHeaderText && jsx(OptionListHeader, {
      children: optionsHeaderText
    }), jsx(OptionList, {
      onMouseLeave: handleOnOptionListMouseLeave,
      maxVisibleOptions: maxVisibleOptions,
      addHalfOption: true,
      children: childrensArray.map(child => {
        if (child.type === OptionGroup) {
          /**
           * We avoid to render the OptionGroup children as those childrens
           * will be rendered next (with the onClick and onMouseEnter events)
           * This clone will only render the label
           */
          return /*#__PURE__*/cloneElement(child, {
            children: undefined,
            value: child.props.value,
            onMouseEnter: () => setFocusedIndex(undefined)
          });
        }
        return /*#__PURE__*/cloneElement(child, {
          onClick: (event, value) => {
            if (value) {
              /**
               * <Option /> value can be a String or IDropdownOption
               * We allow DropdownOptionList to work with both scenarios
               */
              const selectedStringValue = getValueString(value);
              const selectedOption = optionsArray.find(optionArray => optionArray.value === selectedStringValue);
              if (selectedOption) {
                var _child$props$onClick, _child$props;
                (_child$props$onClick = (_child$props = child.props).onClick) === null || _child$props$onClick === void 0 ? void 0 : _child$props$onClick.call(_child$props, event, selectedOption);
                reference === null || reference === void 0 ? void 0 : reference.focus();
                onChange(selectedOption);
                if (resetFocusOnSelect) {
                  setFocusedIndex(undefined);
                }
              }
            }
          },
          focused: isOptionFocused(child),
          key: getValueString(child.props.value),
          onMouseEnter: handleMouseEnter
        });
      })
    })]
  });
}
const DropdownOptionList = /*#__PURE__*/forwardRef(DropdownOptionListForwarded);

export { DropdownOptionList, DropdownOptionListForwarded };
//# sourceMappingURL=DropdownOptionList.js.map
